import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, getArticleById } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <p className="text-lg text-foreground-600 mb-4">文章不存在或已删除</p>
          <Link to="/articles" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
            返回文章列表
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: '精选文章', item: `${siteInfo.url}/articles` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},${siteInfo.keywords}`}
        canonicalUrl={`${siteInfo.url}/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="pt-20 pb-4 bg-background-100 min-h-screen">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '精选文章', href: '/articles' },
            { label: article.title },
          ]}
        />

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          {/* Article Header */}
          <header className="mb-8">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          {/* Cover Image */}
          <div className="aspect-[21/9] rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - ${siteInfo.name}`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-semibold
              prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900
              prose-li:text-foreground-700"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Article Footer */}
          <footer className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-600">分享：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
              <Link
                to="/articles"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <i className="ri-arrow-left-line"></i> 返回文章列表
              </Link>
            </div>
          </footer>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}